/**
 * Stage 5E.2 Phase 3 / Phase 4 — shared CSS token application.
 *
 * Applies the canonical Stage 5E.2 §13.2 token set as CSS custom
 * properties under the ``--brand-*`` namespace.
 *
 * Two targeting modes (both controlled by an optional ``target``
 * element argument):
 *
 *   • DEFAULT (no target) — Company runtime:
 *       tokens → document.documentElement.style
 *       brand-mode-firm class → document.body.classList
 *       (unchanged from Phase 3; the entire Company shell receives
 *        the firm brand globally)
 *
 *   • SCOPED (concrete element passed) — Phase 4 Admin preview:
 *       tokens → target.style
 *       brand-mode-firm class → target.classList
 *       (Admin documentElement / body are NEVER mutated;
 *        CSS custom-property inheritance scopes --brand-* to
 *        the descendant preview subtree only)
 *
 * IMPORTANT (contract §8 / §13.2): semantic product state colours
 * (error, warning, success, Green Lane, Amber Lane, accounting
 * status, document status) remain Ordly-controlled and are NEVER
 * mapped to firm branding. Only the nine brand tokens below are
 * managed here.
 */

const TOKEN_VARS = Object.freeze({
    // ---- Stage 5E.2 legacy 9-key compatibility set (unchanged) ----
    // These continue to drive existing components that consume
    // --brand-primary / --brand-secondary etc. When the backend
    // envelope is v3, the same 9 keys are populated verbatim from
    // ``palette_v3.resolved`` (Slice 2 branching guarantees a
    // single source of truth). Compatibility aliases first.
    primary:           "--brand-primary",
    on_primary:        "--brand-on-primary",
    primary_hover:     "--brand-primary-hover",
    primary_pressed:   "--brand-primary-pressed",
    focus_ring:        "--brand-focus-ring",
    secondary:         "--brand-secondary",
    secondary_surface: "--brand-secondary-surface",
    secondary_border:  "--brand-secondary-border",
    surface_tint:      "--brand-surface-tint",
    // ---- Slice 4 additive v3 23-key semantic set ----------------
    // Only emitted when the envelope is v3; ``brand_*`` keys are
    // present in ``payload.tokens``. Directly relevant runtime
    // surfaces (Company Workspace shell, Accountant Workspace
    // shell, Capture action, page/card surfaces, borders, muted
    // text, focus states) consume these directly.
    brand_primary:            "--brand-primary",
    brand_on_primary:         "--brand-on-primary",
    brand_primary_hover:      "--brand-primary-hover",
    brand_primary_active:     "--brand-primary-active",
    brand_secondary:          "--brand-secondary",
    brand_secondary_subtle:   "--brand-secondary-subtle",
    brand_secondary_border:   "--brand-secondary-border",
    brand_accent:             "--brand-accent",
    brand_accent_subtle:      "--brand-accent-subtle",
    brand_on_accent:          "--brand-on-accent",
    brand_capture:            "--brand-capture",
    brand_capture_hover:      "--brand-capture-hover",
    brand_capture_active:     "--brand-capture-active",
    brand_on_capture:         "--brand-on-capture",
    brand_capture_subtle:     "--brand-capture-subtle",
    brand_background:         "--brand-background",
    brand_surface:            "--brand-surface",
    brand_surface_subtle:     "--brand-surface-subtle",
    brand_surface_tint:       "--brand-surface-tint",
    brand_text:               "--brand-text",
    brand_text_muted:         "--brand-text-muted",
    brand_border:             "--brand-border",
    brand_focus_ring:         "--brand-focus-ring",
});

const BRAND_MODE_CLASS = "brand-mode-firm";

function _resolveTokenTarget(target) {
    // When a concrete target element is supplied, use it for BOTH the
    // CSS vars and the brand-mode-firm class. Otherwise fall back to
    // the global Company-runtime targets.
    if (target && typeof target === "object" && target.style) {
        return { varTarget: target, classTarget: target };
    }
    try {
        return {
            varTarget: document.documentElement,
            classTarget: document.body,
        };
    } catch {
        return { varTarget: null, classTarget: null };
    }
}

export function applyBrandTokens(tokens, target = null) {
    try {
        const { varTarget, classTarget } = _resolveTokenTarget(target);
        if (!varTarget) return;
        if (tokens && typeof tokens === "object") {
            // Two-pass write: because multiple TOKEN_VARS keys can
            // map to the SAME CSS variable (e.g. legacy ``primary``
            // and v3 ``brand_primary`` both write ``--brand-primary``),
            // we must aggregate winners across keys BEFORE writing.
            // A single-pass remove-on-empty would let a later NULL
            // key wipe a value that an earlier key just wrote from
            // the same payload.
            const toSet = new Map();
            for (const [key, cssVar] of Object.entries(TOKEN_VARS)) {
                const v = tokens[key];
                if (typeof v === "string" && v.length > 0) {
                    // Last non-empty write wins per CSS var (v3
                    // brand_* iteration order follows the legacy
                    // 9-key block, so v3 payloads always override
                    // legacy compatibility keys when both are
                    // present in the same envelope).
                    toSet.set(cssVar, v);
                }
            }
            // Apply all winners, remove CSS vars that no key set.
            const cssVarsUniverse = new Set(Object.values(TOKEN_VARS));
            for (const cssVar of cssVarsUniverse) {
                if (toSet.has(cssVar)) {
                    varTarget.style.setProperty(cssVar, toSet.get(cssVar));
                } else {
                    varTarget.style.removeProperty(cssVar);
                }
            }
            classTarget?.classList?.add?.(BRAND_MODE_CLASS);
        } else {
            clearBrandTokens(target);
        }
    } catch {
        // SSR / no-DOM environments — no-op.
    }
}

export function clearBrandTokens(target = null) {
    try {
        const { varTarget, classTarget } = _resolveTokenTarget(target);
        if (!varTarget) return;
        const cssVarsUniverse = new Set(Object.values(TOKEN_VARS));
        for (const cssVar of cssVarsUniverse) {
            varTarget.style.removeProperty(cssVar);
        }
        classTarget?.classList?.remove?.(BRAND_MODE_CLASS);
    } catch {
        // SSR / no-DOM environments — no-op.
    }
}

export { TOKEN_VARS, BRAND_MODE_CLASS };
